/**
 * AWS Cognito Service for Frontend Authentication
 * Handles user authentication with AWS Cognito User Pool
 */

import axios from 'axios';
import DEPLOYMENT_CONFIG from '../config/deployment';

// AWS Cognito Configuration
const COGNITO_CONFIG = {
  region: 'eu-west-3',
  userPoolId: 'eu-west-3_5OCSasKQ2',
  appClientId: '5dnifi08v6kf0ujgmt3gtn1acn',
  appClientSecret: 'nbcn4d1p13isqlmlemnk7ccucm6ukod9eno48og9ulu2boicbq6',
  apiBaseUrl: DEPLOYMENT_CONFIG.getBackendUrl()
};

// Note: SECRET_HASH calculation is handled by the backend
// This is just a placeholder for potential future use

export interface CognitoUser {
  id: number;  // Changed from string to number to match User type
  username: string;
  email: string;
  given_name?: string;
  family_name?: string;
  role?: string;
  business_area?: string;
  tenant?: string;
  email_verified?: boolean;
  avatar_url?: string;
  team_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CognitoAuthResult {
  success: boolean;
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  jwt_token?: string;  // JWT token for API compatibility
  user?: CognitoUser;
  message?: string;
  challenge?: string;
  requiresLogin?: boolean;
  session?: string;
}

class CognitoService {
  private apiClient = axios.create({
    baseURL: COGNITO_CONFIG.apiBaseUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Enable cookies for CSRF
  });

  // CSRF token cache
  private csrfToken: string | null = null;

  // Function to get CSRF token
  private async getCsrfToken(): Promise<string | null> {
    if (this.csrfToken) {
      return this.csrfToken;
    }
    
    try {
      const response = await axios.get(`${COGNITO_CONFIG.apiBaseUrl}/api/csrf-token`, {
        withCredentials: true
      });
      this.csrfToken = response.data.csrf_token;
      return this.csrfToken;
    } catch (error) {
      return null;
    }
  }

  /**
   * Authenticate user with AWS Cognito
   */
  async authenticateUser(username: string, password: string): Promise<CognitoAuthResult> {
    try {
      // Get CSRF token for the request
      const csrfToken = await this.getCsrfToken();
      
      const response = await this.apiClient.post('/api/cognito/login', {
        username,
        password
      }, {
        headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {}
      });

      const result = response.data;
      
      if (result.success && result.access_token) {
        // Clear ALL old authentication data first to prevent using stale tokens
        localStorage.removeItem('cognito_refresh_token');
        localStorage.removeItem('cognito_username');
        localStorage.removeItem('cognito_access_token');
        localStorage.removeItem('cognito_id_token');
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('jwt_token_set_time');
        localStorage.removeItem('user');
        localStorage.removeItem('login_token');
        localStorage.removeItem('login_username');
        
        // Store new tokens in localStorage
        localStorage.setItem('cognito_access_token', result.access_token);
        if (result.id_token) {
          localStorage.setItem('cognito_id_token', result.id_token);
        }
        if (result.refresh_token) {
          localStorage.setItem('cognito_refresh_token', result.refresh_token);
        }
        
        // Store JWT token for API compatibility
        if (result.jwt_token) {
          localStorage.setItem('jwt_token', result.jwt_token);
          // Track when JWT token is set to avoid refresh attempts immediately after login
          localStorage.setItem('jwt_token_set_time', Date.now().toString());
        }
        
        // Store user info
        if (result.user) {
          localStorage.setItem('cognito_user', JSON.stringify(result.user));
        }
        
        // Store the actual Cognito username for SECRET_HASH calculation
        // This is the UUID that Cognito uses, not the email
        if (result.cognito_username) {
          localStorage.setItem('cognito_username', result.cognito_username);
        } else {
          // Fallback to login username if cognito_username not provided
          localStorage.setItem('cognito_username', username);
        }
        
        // Store tenant information for redirection
        if (result.tenant) {
          localStorage.setItem('user_tenant', result.tenant);
        }
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro na autenticação. Tente novamente.'
      };
    }
  }

        /**
         * Refresh access token using refresh token
         */
        async refreshAccessToken(): Promise<CognitoAuthResult> {
          try {
            const refreshToken = localStorage.getItem('cognito_refresh_token');
            const storedUsername = localStorage.getItem('cognito_username');
            
            if (!refreshToken) {
              return { success: false, message: 'No refresh token available' };
            }
            
            if (!storedUsername) {
              return { success: false, message: 'Username not available for token refresh' };
            }

            // Debug logging

            // Get CSRF token for the request
            const csrfToken = await this.getCsrfToken();
            
            const response = await this.apiClient.post('/api/cognito/refresh', {
              refresh_token: refreshToken,
              username: storedUsername  // Always send username for SECRET_HASH calculation
            }, {
              headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {}
            });

      const result = response.data;
      
      if (result.success && result.access_token) {
        localStorage.setItem('cognito_access_token', result.access_token);
        if (result.id_token) {
          localStorage.setItem('cognito_id_token', result.id_token);
        }
      }

      return result;
    } catch (error: any) {
      
      // If it's any authentication error, clear all tokens and force fresh login
      if (error.response?.data?.error_code === 'REQUIRES_FRESH_LOGIN' || 
          error.response?.data?.error_code === 'SECRET_HASH_MISMATCH') {
        this.logout();
        return {
          success: false,
          message: 'Session expired - please login again',
          requiresLogin: true
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao renovar token'
      };
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<CognitoUser | null> {
    try {
      const accessToken = localStorage.getItem('cognito_access_token');
      if (!accessToken) {
        return null;
      }

      const response = await this.apiClient.get('/api/cognito/user', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      return response.data.user || null;
    } catch (error: any) {
      return null;
    }
  }

  /**
   * Verify token validity
   */
  async verifyToken(): Promise<boolean> {
    try {
      const accessToken = localStorage.getItem('cognito_access_token');
      if (!accessToken) {
        return false;
      }

      // Get CSRF token for the request
      const csrfToken = await this.getCsrfToken();
      
      const response = await this.apiClient.post('/api/cognito/verify', {
        token: accessToken
      }, {
        headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {}
      });

      return response.data.success || false;
    } catch (error: any) {
      return false;
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const accessToken = localStorage.getItem('cognito_access_token');
      if (accessToken) {
        // Get CSRF token for the request
        const csrfToken = await this.getCsrfToken();
        
        await this.apiClient.post('/api/cognito/logout', {
          access_token: accessToken
        }, {
          headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {}
        });
      }
    } catch (error: any) {
    } finally {
      // Clear all Cognito-related data from localStorage
      localStorage.removeItem('cognito_access_token');
      localStorage.removeItem('cognito_id_token');
      localStorage.removeItem('cognito_refresh_token');
      localStorage.removeItem('cognito_user');
      localStorage.removeItem('cognito_username');
      localStorage.removeItem('user_tenant');
    }
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('cognito_access_token');
  }

  /**
   * Get stored user data
   */
  getStoredUser(): CognitoUser | null {
    const userData = localStorage.getItem('cognito_user');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  /**
   * Get stored username for SECRET_HASH calculation
   */
  getStoredUsername(): string | null {
    return localStorage.getItem('cognito_username');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getStoredUser();
    return !!(token && user);
  }

  /**
   * Handle new password challenge
   */
  async setNewPassword(session: string, newPassword: string, username: string): Promise<CognitoAuthResult> {
    try {
      // Get CSRF token for the request
      const csrfToken = await this.getCsrfToken();
      
      const response = await this.apiClient.post('/api/cognito/confirm_forgot_password', {
        session,
        new_password: newPassword,
        username: username
      }, {
        headers: csrfToken ? { 'X-CSRFToken': csrfToken } : {}
      });

      const result = response.data;
      
      if (result.success && result.access_token) {
        localStorage.setItem('cognito_access_token', result.access_token);
        if (result.id_token) {
          localStorage.setItem('cognito_id_token', result.id_token);
        }
        if (result.refresh_token) {
          localStorage.setItem('cognito_refresh_token', result.refresh_token);
        }
        if (result.user) {
          localStorage.setItem('cognito_user', JSON.stringify(result.user));
        }
        
        // Store the actual Cognito username for SECRET_HASH calculation
        if (result.cognito_username) {
          localStorage.setItem('cognito_username', result.cognito_username);
        } else {
          // Fallback to login username if cognito_username not provided
          localStorage.setItem('cognito_username', username);
        }
        
        // Store tenant information for redirection
        if (result.tenant) {
          localStorage.setItem('user_tenant', result.tenant);
        }
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao definir nova palavra-passe'
      };
    }
  }
}

// Export singleton instance
export const cognitoService = new CognitoService();
export default cognitoService;
